<template>
  <div class="device-extend-params-config">
    <div class="config-header flex justify-between items-center mb-3">
      <h3 class="text-base font-medium text-gray-800">
        <i class="el-icon-setting mr-2 text-blue-500"></i>
        设备扩展参数配置
      </h3>
      <el-tooltip content="基于设备类型和子类型的默认参数模板进行配置" placement="top">
        <i class="el-icon-question text-gray-400 cursor-help"></i>
      </el-tooltip>
    </div>

    <!-- 默认参数模板信息 -->
    <div v-if="defaultParamsTemplate" class="template-info mb-3 p-2 bg-blue-50 rounded border border-blue-200">
      <div class="flex items-center justify-between">
        <div class="flex items-center">
          <i class="el-icon-document text-blue-600 mr-1"></i>
          <span class="text-xs font-medium text-blue-800">模板</span>
          <el-tag size="mini" class="ml-2" type="info">{{ deviceType }}-{{ deviceSubtype || 'default' }}</el-tag>
        </div>
      </div>
      <div v-if="defaultParamsTemplate.description" class="text-xs text-gray-600 mt-1 line-clamp-1">
        {{ defaultParamsTemplate.description }}
      </div>
    </div>

    <!-- 参数配置表单 -->
    <el-form ref="extendParamsForm" :model="formData" :rules="rules" label-width="90px" size="small">
      <div v-if="!hasValidTemplate" class="no-template-notice p-4 text-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
        <i class="el-icon-warning-outline text-gray-400 text-2xl mb-2"></i>
        <p class="text-gray-500">暂无适用的默认参数模板</p>
        <p class="text-xs text-gray-400 mt-1">需要先配置设备类型为"{{ deviceType }}"的默认参数模板</p>
      </div>

      <div v-else>
        <!-- 左右分栏布局：表单 2 : JSON 1 -->
        <div class="params-layout">
          <!-- 左侧表单区域 -->
          <div class="form-section">
            <div class="params-grid">
              <div v-for="(param, key) in visibleParams" :key="key" class="param-item">
                <!-- 参数标签行 -->
                <div class="param-label-row">
                  <span class="param-label-text">{{ getParamLabel(key) }}</span>
                  <el-tooltip v-if="getParamDescription(key)" :content="getParamDescription(key)" placement="top">
                    <i class="el-icon-info text-gray-400 cursor-help ml-1"></i>
                  </el-tooltip>
                </div>
                
                <!-- 参数输入行 -->
                <div class="param-input-row">
                  
                  <!-- 布尔型参数 -->
                  <el-switch
                    v-if="isBoolean(param.value)"
                    v-model="parsedExtendParams[key].value"
                    size="small"
                    @change="handleParamChange"
                  ></el-switch>
                  
                  <!-- 数字型参数 -->
                  <el-input-number
                    v-else-if="isNumber(param.value)"
                    v-model="parsedExtendParams[key].value"
                    :min="param.min || -999999"
                    :max="param.max || 999999"
                    :step="param.step || 1"
                    :precision="param.precision || 0"
                    size="small"
                    controls-position="right"
                    @change="handleParamChange"
                    class="w-full"
                  ></el-input-number>
                  
                  <!-- 选择型参数 -->
                  <el-select
                    v-else-if="param.options && param.options.length > 0"
                    v-model="parsedExtendParams[key].value"
                    placeholder="请选择"
                    size="small"
                    @change="handleParamChange"
                    class="w-full"
                  >
                    <el-option
                      v-for="option in param.options"
                      :key="option.value"
                      :label="option.label"
                      :value="option.value"
                    ></el-option>
                  </el-select>
                  
                  <!-- 文本型参数 -->
                  <el-input
                    v-else
                    v-model="parsedExtendParams[key].value"
                    :placeholder="param.placeholder || `请输入${getParamLabel(key)}`"
                    :maxlength="param.maxLength || 255"
                    :type="param.type === 'textarea' ? 'textarea' : 'text'"
                    :rows="param.rows || 2"
                    size="small"
                    @input="handleParamChange"
                  ></el-input>
                </div>
              </div>
            </div>
          </div>
          
          <!-- 右侧JSON显示区域 -->
          <div class="json-section">
            <div class="json-header">
              <span class="json-title">JSON配置</span>
              <el-tooltip content="实时显示当前参数的JSON格式" placement="top">
                <i class="el-icon-info text-gray-400 cursor-help"></i>
              </el-tooltip>
            </div>
            <div class="json-content">
              <pre class="json-display">{{ formattedJsonString }}</pre>
            </div>
            <div class="json-validation mt-2" v-if="jsonError">
              <el-alert
                :title="jsonError"
                type="error"
                :closable="false"
                size="small"
              ></el-alert>
            </div>
          </div>
        </div>
      </div>
    </el-form>

    <!-- 操作按钮 -->
    <div v-if="showActions" class="config-actions flex justify-end pt-3 border-t border-gray-200">
      <el-button size="small" @click="handleReset" :disabled="!hasValidTemplate">
        <i class="el-icon-refresh-left mr-1"></i>
        重置
      </el-button>
      <el-button type="primary" size="small" @click="handleSave" :loading="saving" :disabled="!hasValidTemplate || !!jsonError">
        <i class="el-icon-check mr-1"></i>
        保存
      </el-button>
    </div>
    
    <!-- 仅显示重置按钮（嵌入模式） -->
    <div v-else class="config-actions-inline flex justify-end mt-3">
      <el-button size="mini" @click="handleReset" :disabled="!hasValidTemplate">
        <i class="el-icon-refresh-left mr-1"></i>
        重置
      </el-button>
    </div>
  </div>
</template>

<script>
import deviceDefaultParamsAPI from '@/api/device-default-params'
import { validateExtendParamsJson, mergeParams, formatExtendParamsForDisplay } from '@/utils/extendParamsUtils'

export default {
  name: 'DeviceExtendParamsConfig',
  props: {
    deviceData: {
      type: Object,
      default: () => ({})
    },
    deviceType: {
      type: String,
      required: true
    },
    deviceSubtype: {
      type: String,
      default: 'default'
    },
    showActions: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      defaultParamsTemplate: null,
      parsedExtendParams: {},
      formData: {},
      rules: {},
      saving: false,
      showJsonEditor: false,
      jsonString: '{}',
      jsonError: null,
      hasValidTemplate: false
    }
  },
  mounted() {
    this.loadDefaultParamsTemplate()
  },
  watch: {
    deviceType: {
      handler() {
        this.loadDefaultParamsTemplate()
      },
      immediate: true
    },
    deviceSubtype: {
      handler() {
        this.loadDefaultParamsTemplate()
      }
    },
    deviceData: {
      handler() {
        this.initializeExtendParams()
      },
      deep: true
    }
  },
  methods: {
    /**
     * 加载默认参数模板
     */
    async loadDefaultParamsTemplate() {
      if (!this.deviceType) return

      try {
        const response = await deviceDefaultParamsAPI.getByDeviceTypeAndSubtype(
          this.deviceType,
          this.deviceSubtype
        )
        
        if (response.code === 200 && response.data) {
          this.defaultParamsTemplate = response.data
          this.hasValidTemplate = true
          this.initializeExtendParams()
        } else {
          this.defaultParamsTemplate = null
          this.hasValidTemplate = false
          this.parsedExtendParams = {}
        }
      } catch (error) {
        console.error('加载默认参数模板失败:', error)
        this.defaultParamsTemplate = null
        this.hasValidTemplate = false
        this.parsedExtendParams = {}
      }
    },

    /**
     * 初始化扩展参数
     */
    initializeExtendParams() {
      if (!this.hasValidTemplate || !this.defaultParamsTemplate) {
        return
      }

      try {
        // 解析默认参数模板
        const defaultParams = JSON.parse(this.defaultParamsTemplate.defaultParams || '{}')
        
        // 解析当前设备的扩展参数
        const currentExtendParams = this.deviceData.extend_params 
          ? JSON.parse(this.deviceData.extend_params)
          : {}

        // 合并默认参数和当前参数
        this.parsedExtendParams = {}
        
        Object.keys(defaultParams).forEach(key => {
          const defaultValue = defaultParams[key]
          const currentValue = currentExtendParams[key]
          
          this.parsedExtendParams[key] = {
            value: currentValue !== undefined ? currentValue : defaultValue,
            defaultValue: defaultValue,
            ...this.getParamConfig(key, defaultValue)
          }
        })

        // 更新JSON字符串
        this.updateJsonString()
        
        // 如果设备的extend_params为null/undefined，且有模板默认值，立即将默认值作为设备的扩展参数
        const isExtendParamsNull = this.deviceData.extend_params == null // 使用 == 来检查 null 和 undefined
        
        if (isExtendParamsNull && Object.keys(defaultParams).length > 0) {
          // 延迟触发change事件，确保组件完全初始化
          this.$nextTick(() => {
            this.$emit('change', this.getExtendParamsValue())
          })
        }
      } catch (error) {
        console.error('初始化扩展参数失败:', error)
        this.parsedExtendParams = {}
      }
    },

    /**
     * 获取参数配置信息
     */
    getParamConfig(key, defaultValue) {
      // 根据参数名称和值类型推断配置
      const config = {
        type: 'text',
        placeholder: '',
        description: '',
        options: []
      }

      // 根据键名推断配置
      switch (key.toLowerCase()) {
        case 'displaymode':
          config.options = [
            { label: '全屏显示', value: 'fullscreen' },
            { label: '窗口显示', value: 'window' },
            { label: '小窗显示', value: 'mini' }
          ]
          config.description = '设备显示模式'
          break
        case 'autorefresh':
          config.description = '是否自动刷新数据'
          break
        case 'refreshinterval':
          config.min = 1
          config.max = 3600
          config.step = 1
          config.description = '自动刷新间隔（秒）'
          break
        case 'x':
        case 'y':
          config.min = -999999
          config.max = 999999
          config.step = 1
          config.description = key === 'x' ? 'X坐标位置' : 'Y坐标位置'
          break
        case 'width':
        case 'height':
          config.min = 1
          config.max = 999999
          config.step = 1
          config.description = key === 'width' ? '显示宽度' : '显示高度'
          break
        case 'elevationdeg':
          config.min = -90
          config.max = 90
          config.step = 0.1
          config.precision = 2
          config.description = '激光束仰角（度），水平=0，俯角为负'
          break
        case 'azimuthdeg':
          config.min = 0
          config.max = 360
          config.step = 0.1
          config.precision = 2
          config.description = '指北方位角（度），顺时针0-360'
          break
        case 'rangem':
          config.min = 1
          config.max = 5000
          config.step = 1
          config.description = '激光视距/量程（米），默认2000'
          break
        case 'bgtolerancem':
          config.min = 0
          config.max = 200
          config.step = 0.1
          config.precision = 2
          config.description = '背景距离判定容差（米）'
          break
        case 'backgrounddistances':
          config.type = 'text'
          config.placeholder = '逗号分隔，如 144.8,500'
          config.description = '背景距离集合（米），逗号分隔多个；读数命中即视为无目标背景'
          break
        case 'detectmode':
          config.options = [
            { label: 'A - 水平单光束(持续遮挡)', value: 'A' },
            { label: 'B - 角度扫描(平滑轨迹)', value: 'B' }
          ]
          config.description = '检测模式：默认 A（固定水平单光束，回波被持续压近即判超高）'
          break
        case 'obstructionminframes':
          config.min = 2
          config.max = 20
          config.step = 1
          config.onlyMode = 'A'
          config.description = '模式A：连续遮挡帧数下限（2~20），越小越灵敏、越大越严格'
          break
        case 'sectorhalfangledeg':
          config.min = 0
          config.max = 30
          config.step = 1
          config.precision = 0
          config.onlyMode = 'A'
          config.description = '模式A：扇形搜索半角（0~30°，0=关闭扇形模糊匹配，仅用射线点匹配）'
          break
        default:
          if (typeof defaultValue === 'boolean') {
            config.description = '布尔类型参数'
          } else if (typeof defaultValue === 'number') {
            config.min = -999999
            config.max = 999999
            config.step = 1
            config.description = '数值类型参数'
          } else {
            config.description = '文本类型参数'
          }
      }

      return config
    },

    /**
     * 获取参数显示标签
     */
    getParamLabel(key) {
      const labelMap = {
        'displayMode': '显示模式',
        'autoRefresh': '自动刷新',
        'refreshInterval': '刷新间隔',
        'x': 'X坐标',
        'y': 'Y坐标',
        'width': '宽度',
        'height': '高度',
        'elevationDeg': '仰角(度)',
        'azimuthDeg': '指北方位角(度)',
        'rangeM': '视距(米)',
        'backgroundDistances': '背景距离(米)',
        'bgToleranceM': '背景容差(米)',
        'detectMode': '检测模式',
        'obstructionMinFrames': '连续遮挡帧数',
        'sectorHalfAngleDeg': '扇形半角(度)'
      }
      if (labelMap[key]) return labelMap[key]
      const lower = String(key).toLowerCase()
      const hit = Object.keys(labelMap).find(k => k.toLowerCase() === lower)
      return hit ? labelMap[hit] : key
    },

    /**
     * 获取参数描述
     */
    getParamDescription(key) {
      const param = this.parsedExtendParams[key]
      return param ? param.description : ''
    },

    /**
     * 判断是否为布尔型
     */
    isBoolean(value) {
      return typeof value === 'boolean'
    },

    /**
     * 判断是否为数字型
     */
    isNumber(value) {
      return typeof value === 'number'
    },

    /**
     * 处理参数变更
     */
    handleParamChange() {
      this.updateJsonString()
      this.$emit('change', this.getExtendParamsValue())
    },

    /**
     * 更新JSON字符串
     */
    updateJsonString() {
      try {
        const params = {}
        Object.keys(this.parsedExtendParams).forEach(key => {
          params[key] = this.parsedExtendParams[key].value
        })
        this.jsonString = JSON.stringify(params, null, 2)
        this.jsonError = null
      } catch (error) {
        this.jsonError = 'JSON格式化失败: ' + error.message
      }
    },

    /**
     * 处理JSON变更
     */
    handleJsonChange() {
      const validation = validateExtendParamsJson(this.jsonString)
      
      if (validation.valid) {
        this.jsonError = null
        
        // 更新参数值
        Object.keys(this.parsedExtendParams).forEach(key => {
          if (validation.data.hasOwnProperty(key)) {
            this.parsedExtendParams[key].value = validation.data[key]
          }
        })
        
        this.$emit('change', this.getExtendParamsValue())
      } else {
        this.jsonError = 'JSON格式错误: ' + validation.error
      }
    },

    /**
     * 切换JSON编辑器
     */
    toggleJsonEditor() {
      this.showJsonEditor = !this.showJsonEditor
      if (this.showJsonEditor) {
        this.updateJsonString()
      }
    },

    /**
     * 重置为默认值
     */
    handleReset() {
      Object.keys(this.parsedExtendParams).forEach(key => {
        this.parsedExtendParams[key].value = this.parsedExtendParams[key].defaultValue
      })
      this.updateJsonString()
      this.$emit('change', this.getExtendParamsValue())
      this.$message.success('已重置为默认配置')
    },

    /**
     * 保存配置
     */
    async handleSave() {
      if (this.jsonError) {
        this.$message.error('请修正JSON格式错误后再保存')
        return
      }

      this.saving = true
      try {
        const extendParams = this.getExtendParamsValue()
        this.$emit('save', extendParams)
        this.$message.success('扩展参数配置已保存')
      } catch (error) {
        console.error('保存扩展参数失败:', error)
        this.$message.error('保存失败: ' + error.message)
      } finally {
        this.saving = false
      }
    },

    /**
     * 获取扩展参数值
     */
    getExtendParamsValue() {
      const params = {}
      Object.keys(this.parsedExtendParams).forEach(key => {
        params[key] = this.parsedExtendParams[key].value
      })
      return JSON.stringify(params)
    },

    /**
     * 验证配置
     */
    validateConfig() {
      return !this.jsonError && this.hasValidTemplate
    }
  },
  
  computed: {
    /**
     * 按当前"检测模式"过滤展示的参数：
     * 带 onlyMode 标记的参数只在对应检测模式下显示（例如"连续遮挡帧数"仅模式A适用），
     * 避免把运维不需要理解的算法参数全量暴露。
     */
    visibleParams() {
      const params = this.parsedExtendParams || {}
      const modeItem = params.detectMode
      const mode = modeItem ? modeItem.value : null
      // 展示顺序：检测模式置顶，其余按运维习惯排序；未列出的参数保持原顺序
      const preferred = ['detectMode', 'elevationDeg', 'azimuthDeg', 'rangeM',
        'backgroundDistances', 'sectorHalfAngleDeg', 'obstructionMinFrames', 'bgToleranceM']
      const out = {}
      Object.keys(params)
        .sort((a, b) => {
          const ia = preferred.indexOf(a)
          const ib = preferred.indexOf(b)
          return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib)
        })
        .forEach(key => {
          const p = params[key]
          if (p && p.onlyMode && mode && p.onlyMode !== mode) return
          out[key] = p
        })
      return out
    },

    /**
     * 格式化的JSON字符串（用于显示）
     */
    formattedJsonString() {
      try {
        const params = {}
        Object.keys(this.parsedExtendParams).forEach(key => {
          params[key] = this.parsedExtendParams[key].value
        })
        return JSON.stringify(params, null, 2)
      } catch (error) {
        return '格式化错误'
      }
    }
  }
}
</script>

<style scoped>
.device-extend-params-config {
  background: white;
  border-radius: 6px;
  padding: 16px;
}

.config-header h3 {
  margin: 0;
  color: #2c3e50;
}

.template-info {
  background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
}

/* 左右分栏布局 */
.params-layout {
  display: flex;
  gap: 16px;
  min-height: 200px;
}

.form-section {
  flex: 2;
  min-width: 0; /* 防止flex子项溢出 */
}

.json-section {
  flex: 1;
  min-width: 200px;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  background-color: #fafbfc;
  display: flex;
  flex-direction: column;
}

/* 表单网格布局 - 每行两个 */
.params-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 16px;
}

.param-item {
  padding: 8px;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  background-color: #fafbfc;
  transition: all 0.2s ease;
}

.param-item:hover {
  border-color: #409eff;
  box-shadow: 0 1px 4px rgba(64, 158, 255, 0.1);
}

.param-label-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
  min-height: 20px;
}

.param-label-text {
  font-size: 12px;
  font-weight: 500;
  color: #606266;
  line-height: 1.2;
}

.param-input-row {
  width: 100%;
}

/* JSON显示区域 */
.json-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid #e4e7ed;
  background-color: #f5f7fa;
  border-radius: 6px 6px 0 0;
}

.json-title {
  font-size: 12px;
  font-weight: 500;
  color: #606266;
}

.json-content {
  flex: 1;
  padding: 12px;
  overflow: hidden;
}

.json-display {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 11px;
  line-height: 1.4;
  color: #2c3e50;
  background: transparent;
  margin: 0;
  padding: 0;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 300px;
  overflow-y: auto;
}

/* 响应式布局 */
@media (max-width: 768px) {
  .params-layout {
    flex-direction: column;
    gap: 12px;
  }
  
  .params-grid {
    grid-template-columns: 1fr;
    gap: 8px;
  }
  
  .json-section {
    min-height: 150px;
  }
}

.param-label {
  font-weight: 500;
  color: #606266;
}

.config-actions {
  background: #fafafa;
  margin: 20px -20px -20px;
  padding: 16px 20px;
  border-radius: 0 0 8px 8px;
}

.config-actions-inline {
  margin-top: 16px;
}

.no-template-notice {
  margin: 20px 0;
}

.cursor-help {
  cursor: help;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .device-extend-params-config {
    padding: 16px;
  }
  
  .config-actions {
    margin: 16px -16px -16px;
    padding: 12px 16px;
  }
}

/* ElementUI组件样式调整 */
.el-form-item {
  margin-bottom: 16px;
}

/* 自定义滚动条 */
.json-display::-webkit-scrollbar {
  width: 6px;
}

.json-display::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.json-display::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.json-display::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* 表单控件样式优化 */
.param-input-row .el-input__inner,
.param-input-row .el-input-number__inner,
.param-input-row .el-select .el-input__inner {
  height: 28px !important;
  line-height: 28px !important;
}

.param-input-row .el-switch {
  margin: 2px 0;
}

.param-input-row .el-input-number {
  width: 100%;
}

.param-input-row .el-select {
  width: 100%;
}
</style>
